import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators'

import CoreStoreModuleContainer from './CoreStoreModuleContainer'
import { Client as RevasClient } from '@revas/ts-api'

import AppError from '@revas/kadra-library/src/core/app/vue/vuex/AppError'
import AppStoreModule from '@revas/kadra-library/src/core/app/vue/vuex/AppStoreModule'

import User from './models/User'
import GetUserMutationPayload from './models/user/GetUserMutationPayload'

import { Organization } from './models/Organization'

import App from './models/App'
import PaginationState from './models/pagination/PaginationState'
import Page from './models/pagination/Page'
import ListPinnedAppsPagination from './models/launchpad/ListPinnedAppsPagination'
import ListPinnedAppsActionPayload from './models/launchpad/ListPinnedAppsActionPayload'
import ListPinnedAppsMutationPayload from './models/launchpad/ListPinnedAppsMutationPayload'

@Module({
  name: 'core',
  namespaced: true,
  stateFactory: true
})
export default class CoreStoreModule extends VuexModule {
  private _appStoreModule: AppStoreModule | null = null
  private _revasClient: RevasClient | null = null

  @Mutation
  public inject (container: CoreStoreModuleContainer) {
    this._appStoreModule = container.appStoreModule
    this._revasClient = container.revasClient
  }

  // Current User
  private _profile: User | null = null

  get userProfile (): User | null {
    return this._profile
  }

  @Mutation
  private getProfileRequest () {

  }
  @Mutation
  private getProfileSuccess (payload: GetUserMutationPayload) {
    this._profile = payload.user
  }

  @Action({ rawError: true })
  async asyncGetUserProfile (): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    this.getProfileRequest()
    try {
      const response = await this._revasClient.asyncRevasPost('/users/users.GetUser/', {
        profilesAliases: [
          'me'
        ]
      })
      const payload = new GetUserMutationPayload(response.user)
      this.getProfileSuccess(payload)
    } catch (error) {
      // if (error.code !== 'User not found') {
      //   const errorPayload = new AppError()
      //   errorPayload.error = error
      //   this._appStoreModule.asyncPushError(error)
      // }
      const payload = new GetUserMutationPayload(null)
      this.getProfileSuccess(payload)
    }
  }

  // Current user organizations
  private _organizations: Organization[] | null = null
  private _activeOrganization: Organization | null = null

  get userOrganizations (): Organization[] | null {
    return this._organizations
  }

  get activeUserOrganization (): Organization | null {
    return this._activeOrganization
  }

  @Mutation
  listUserOrganizationSuccess (organizations: Organization[]) {
    this._organizations = organizations
  }

  @Mutation
  changeActiveOrganization (payload: ChangeActiveOrganizationPayload) {
    const organization = this._organizations!.filter((o) => {
      return o.id === payload.organizationId
    })
    if (organization.length > 0) {
      this._activeOrganization = organization[0]
    }
  }

  @Action({ rawError: true })
  async asyncListUserOrganizations (): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    if (this._profile === null) {
      await this.asyncGetUserProfile()
    }
    if (this._profile === null) {
      return
    }
    try {
      let nextPageToken = '-'
      const organizations: Organization[] = []

      while (nextPageToken !== '') {
        const response = await this._revasClient.asyncRevasPost('/organizations/organizations.ListOrganizations/', {
          pageSize: 20,
          pageToken: nextPageToken === '-' ? '' : nextPageToken,
          search: `eq[userId]=${this._profile.id}`
        })
        response.organizations.forEach((organization: Organization) => {
          organizations.push(organization)
        })
        nextPageToken = response.nextPageToken
      }
      this.listUserOrganizationSuccess(organizations)
    } catch (error) {
      // TODO if (error.code !== 'User not found') {
      //   const errorPayload = new AppError()
      //   errorPayload.error = error
      //   this._appStoreModule.asyncPushError(error)
      // }
    }
  }

  @Action({ rawError: true })
  async asyncChangeActiveOrganization (payload: ChangeActiveOrganizationPayload): Promise<void> {
    this.changeActiveOrganization(payload)
  }

  // List Pinned Apps
  private _currentPinnedApps: PaginationState<App> = new ListPinnedAppsPagination(this._revasClient!, 0, '-')

  public get currentAppsItems (): App[] | null {
    return this._currentPinnedApps.getCurrentPage().items
  }

  public get currentAppsPage (): number {
    return this._currentPinnedApps.getCurrentPage().number
  }

  public get currentAppHasNextPage (): boolean {
    return this._currentPinnedApps.hasNextPage()
  }

  @Mutation
  private listAppsSuccess (payload: ListPinnedAppsMutationPayload): void {
    this._currentPinnedApps = payload.pagination
  }

  @Action({ rawError: true })
  public async asyncListPinnedApps (payload: ListPinnedAppsActionPayload): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    let pagination: PaginationState<App>
    const page = new Page<App>()
    page.number = 1
    page.search = `eq[appName]=${payload.appName}`

    const PAGE_SIZE = 100
    pagination = new ListPinnedAppsPagination(this._revasClient, PAGE_SIZE, page.search)
    try {
      await pagination.asyncChangeCurrentPage(page.number)
      const listAppsMutationPayload = new ListPinnedAppsMutationPayload(pagination)
      this.listAppsSuccess(listAppsMutationPayload)
    } catch (error) {
    }
  }

  //
  //
  // List apps
  private _currentFavoriteApps: FavoriteApp[] = []
  public get currentFavoriteApps (): FavoriteApp[] {
    if (this._currentFavoriteApps.length === 0) {
      return []
    }
    const favorites = this._currentFavoriteApps.filter(a => a.positionY !== 0).sort(function (a: FavoriteApp, b: FavoriteApp) { return a.positionY - b.positionY })
    return favorites.slice(0, 5)
  }

  @Mutation
  private setApps (payload: { apps: FavoriteApp[] }): void {
    this._currentFavoriteApps = payload.apps
  }

  @Action({ rawError: true })
  public async asyncListFavoriteApps (payload: {}): Promise<void> {
    if (this._appStoreModule === null) {
      throw new Error('App Store module is null')
    }
    if (this._revasClient === null) {
      const errorPayload = new AppError()
      this._appStoreModule.asyncPushError(errorPayload)
      return
    }

    try {
      const response = await this._revasClient.asyncRevasPost('/desktops/desktops.ListApps/', {
        pageSize: 0,
        pageToken: '',
        search: ''
      })
      this.setApps({ apps: response.apps })
    } catch (error) {
    }
  }
}

export class ChangeActiveOrganizationPayload {
  organizationId: string = '-'
}

export interface FavoriteApp {
  id: string;
  organizationId: string;
  userId: string;
  packageName: string;
  name: string;
  title: string;
  description: string;
  icon: string;
  url: string;
  positionY: number;
}
